import { CircleCheck, CircleX, Clock, FlaskConical, LoaderCircle } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/_shared/components/ui/card';

import type { WorkflowRun } from '../types';

function runStatusIcon(status: string): React.ReactElement {
  switch (status) {
    case 'completed':
      return <CircleCheck size={14} className="text-green-600" />;
    case 'failed':
      return <CircleX size={14} className="text-red-500" />;
    case 'running':
      return <LoaderCircle size={14} className="animate-spin text-blue-500" />;
    case 'dry_run':
      return <FlaskConical size={14} className="text-amber-500" />;
    default:
      return <Clock size={14} className="text-muted-foreground" />;
  }
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '\u2014';
  return new Date(dateStr).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function RunHistory({ runs, isLoading }: { runs: WorkflowRun[]; isLoading: boolean }): React.ReactElement {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Run History</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading && (
          <div className="flex justify-center py-4">
            <LoaderCircle size={16} className="animate-spin text-muted-foreground" />
          </div>
        )}

        {!isLoading && runs.length === 0 && (
          <p className="py-4 text-center text-xs text-muted-foreground">
            No runs yet. Click &quot;Run Now&quot; or &quot;Dry Run&quot; to test.
          </p>
        )}

        {!isLoading && runs.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="pr-4 pb-2">Status</th>
                  <th className="pr-4 pb-2">Trigger</th>
                  <th className="pr-4 pb-2">Rows</th>
                  <th className="pr-4 pb-2">Actions</th>
                  <th className="pr-4 pb-2">Skipped</th>
                  <th className="pr-4 pb-2">Failed</th>
                  <th className="pb-2">Started</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((run) => (
                  <tr key={run.id} className="border-b border-border/50 last:border-0">
                    <td className="py-2 pr-4">
                      <div className="flex items-center gap-1.5">
                        {runStatusIcon(run.status)}
                        <span>{run.status}</span>
                      </div>
                    </td>
                    <td className="py-2 pr-4">{run.trigger}</td>
                    <td className="py-2 pr-4 font-mono">{run.rowsQueried}</td>
                    <td className="py-2 pr-4 font-mono">{run.actionsTaken}</td>
                    <td className="py-2 pr-4 font-mono">{run.rowsSkipped}</td>
                    <td className="py-2 pr-4 font-mono">
                      {run.actionsFailed > 0 ? (
                        <span className="text-red-500">{run.actionsFailed}</span>
                      ) : (
                        run.actionsFailed
                      )}
                    </td>
                    <td className="py-2">{formatDate(run.startedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!isLoading && runs.some((r) => r.error) && (
          <div className="mt-3 space-y-2">
            {runs
              .filter((r) => r.error)
              .slice(0, 3)
              .map((r) => (
                <div key={r.id} className="rounded bg-red-50 p-2 text-xs text-red-700">
                  <span className="font-medium">Error ({formatDate(r.startedAt)}):</span> {r.error}
                </div>
              ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
